import React, { useState, useEffect, FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { RichTextEditor } from '../components/RichTextEditor';

interface AgendaItem {
  id: string;
  eventId: string;
  title: string;
  description: string | null;
  details: string | null;
  startTime: string;
  endTime: string;
  speaker: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface AgendaFormData {
  title: string;
  description: string;
  details: string;
  startTime: string;
  endTime: string;
  speaker: string;
  sortOrder: string;
}

const emptyForm: AgendaFormData = {
  title: '',
  description: '',
  details: '',
  startTime: '',
  endTime: '',
  speaker: '',
  sortOrder: '0',
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '2rem',
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: {
    color: '#1a6b3c',
    fontSize: '1.75rem',
    margin: 0,
  },
  backLink: {
    fontSize: '0.85rem',
    color: '#1a6b3c',
    textDecoration: 'none',
    marginBottom: '0.5rem',
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '44px',
    minWidth: '44px',
    boxSizing: 'border-box',
  },
  addButton: {
    padding: '0.6rem 1.2rem',
    minHeight: '44px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  loading: {
    color: '#666',
    textAlign: 'center' as const,
    padding: '2rem',
  },
  empty: {
    color: '#666',
    textAlign: 'center' as const,
    padding: '2rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px dashed #ccc',
  },
  itemCard: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '1rem 1.25rem',
    marginBottom: '0.75rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  itemTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: '#0f2d1f',
    marginBottom: '0.25rem',
  },
  itemTime: {
    fontSize: '0.85rem',
    color: '#1a6b3c',
    fontWeight: 500,
    marginBottom: '0.25rem',
  },
  itemDescription: {
    fontSize: '0.9rem',
    color: '#444',
    marginTop: '0.4rem',
    lineHeight: 1.4,
  },
  itemSpeaker: {
    fontSize: '0.85rem',
    color: '#666',
    marginTop: '0.3rem',
    fontStyle: 'italic',
  },
  actions: {
    display: 'flex',
    gap: '0.25rem',
    alignItems: 'center',
    flexShrink: 0,
  },
  actionBtn: {
    padding: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    lineHeight: 1,
    minWidth: '44px',
    minHeight: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '2rem',
    width: '92%',
    maxWidth: '900px',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  modalTitle: {
    color: '#1a6b3c',
    marginBottom: '1.25rem',
    fontSize: '1.25rem',
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    minHeight: '44px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    minHeight: '80px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
    resize: 'vertical' as const,
    fontFamily: 'inherit',
  },
  submitBtn: {
    width: '100%',
    padding: '0.75rem',
    minHeight: '44px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
    boxSizing: 'border-box',
  },
  cancelBtn: {
    width: '100%',
    padding: '0.75rem',
    minHeight: '44px',
    backgroundColor: '#eee',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    marginTop: '0.5rem',
    boxSizing: 'border-box',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
};

function formatAgendaTime(isoStr: string): string {
  if (!isoStr) return '';
  let hours: number, minutes: number;
  if (isoStr.includes('T')) {
    const timePart = isoStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (isoStr.includes(':')) {
    [hours, minutes] = isoStr.split(':').map(Number);
  } else {
    return isoStr;
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

interface EventDetail {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  venue?: { name: string; address?: string; city?: string; state?: string; parkingInfo?: string | null; contactName?: string | null; contactPhone?: string | null };
  mealInfo?: string | null;
  transportInfo?: string | null;
}

export function EventAgendaPage() {
  const { id: eventId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const canManage = user ? isAtLeast(user.role, 'Organizer') : false;

  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventDetail, setEventDetail] = useState<EventDetail | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<AgendaItem | null>(null);
  const [form, setForm] = useState<AgendaFormData>(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  function toggleExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function fetchAgenda() {
    if (!eventId) return;
    setLoading(true);
    try {
      const [agendaData, eventData] = await Promise.all([
        apiClient<AgendaItem[]>(`/events/${eventId}/agenda`),
        apiClient<EventDetail>(`/events`).then((events: unknown) => {
          const allEvents = events as EventDetail[];
          return allEvents.find((e) => e.id === eventId) || null;
        }).catch(() => null),
      ]);
      setAgendaItems(agendaData);
      setEventDetail(eventData);
    } catch {
      setAgendaItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAgenda();
  }, [eventId]);

  function openCreate() {
    // Default the date portion to the event's date (Eastern time, no UTC conversion)
    let defaultStart = '';
    let defaultEnd = '';
    if (eventDetail?.date) {
      // Extract just the date part without timezone conversion
      const datePart = eventDetail.date.includes('T') ? eventDetail.date.split('T')[0] : eventDetail.date.slice(0, 10);
      defaultStart = datePart + 'T12:00';
      defaultEnd = datePart + 'T13:00';
    }
    setForm({
      ...emptyForm,
      sortOrder: String(agendaItems.length),
      startTime: defaultStart,
      endTime: defaultEnd,
    });
    setEditingItem(null);
    setFormError('');
    setShowForm(true);
  }

  function openEdit(item: AgendaItem) {
    setForm({
      title: item.title,
      description: item.description || '',
      details: item.details || '',
      startTime: item.startTime ? item.startTime.slice(0, 16) : '',
      endTime: item.endTime ? item.endTime.slice(0, 16) : '',
      speaker: item.speaker || '',
      sortOrder: String(item.sortOrder),
    });
    setEditingItem(item);
    setFormError('');
    setShowForm(true);
  }

  async function handleDelete(item: AgendaItem) {
    if (!confirm(`Delete "${item.title}" from the agenda?`)) return;
    try {
      await apiClient(`/events/${eventId}/agenda/${item.id}`, { method: 'DELETE' });
      setAgendaItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      alert(apiError.message || 'Failed to delete agenda item.');
    }
  }

  async function handleReorder(item: AgendaItem, direction: 'up' | 'down') {
    const currentIndex = agendaItems.findIndex((i) => i.id === item.id);
    const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (swapIndex < 0 || swapIndex >= agendaItems.length) return;

    const swapItem = agendaItems[swapIndex];
    const newItems = [...agendaItems];
    // Swap sortOrder values
    const tempOrder = newItems[currentIndex].sortOrder;
    newItems[currentIndex] = { ...newItems[currentIndex], sortOrder: newItems[swapIndex].sortOrder };
    newItems[swapIndex] = { ...newItems[swapIndex], sortOrder: tempOrder };
    // Swap positions in array
    [newItems[currentIndex], newItems[swapIndex]] = [newItems[swapIndex], newItems[currentIndex]];
    setAgendaItems(newItems);

    try {
      await apiClient(`/events/${eventId}/agenda/reorder`, {
        method: 'PATCH',
        body: {
          items: [
            { id: item.id, sortOrder: newItems.find((i) => i.id === item.id)!.sortOrder },
            { id: swapItem.id, sortOrder: newItems.find((i) => i.id === swapItem.id)!.sortOrder },
          ],
        },
      });
    } catch {
      // Revert on failure
      fetchAgenda();
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');

    if (!form.title || !form.startTime || !form.endTime) {
      setFormError('Title, start time, and end time are required.');
      return;
    }

    setSaving(true);

    const payload = {
      title: form.title,
      description: form.description || undefined,
      details: form.details, // send as-is (empty string clears it on edit)
      startTime: form.startTime.includes('T') ? form.startTime + ':00.000Z' : form.startTime,
      endTime: form.endTime.includes('T') ? form.endTime + ':00.000Z' : form.endTime,
      speaker: form.speaker || undefined,
      sortOrder: parseInt(form.sortOrder, 10) || 0,
    };

    try {
      if (editingItem) {
        const updated = await apiClient<AgendaItem>(`/events/${eventId}/agenda/${editingItem.id}`, {
          method: 'PATCH',
          body: payload,
        });
        setAgendaItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      } else {
        const created = await apiClient<AgendaItem>(`/events/${eventId}/agenda`, {
          method: 'POST',
          body: payload,
        });
        setAgendaItems((prev) => [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder));
      }
      setShowForm(false);
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      setFormError(apiError.message || 'Failed to save agenda item.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="attendee-page event-agenda-page" style={styles.page}>
      <Link to="/events" style={styles.backLink} className="touch-target">← Back to Events</Link>

      {/* Event Overview Section */}
      {eventDetail && (
        <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#0f2d1f', margin: '0 0 0.5rem' }}>{eventDetail.name}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.9rem', color: '#374151' }}>
            <span>🕐 {formatAgendaTime(eventDetail.startTime)} – {formatAgendaTime(eventDetail.endTime)}</span>
            {eventDetail.venue && (
              <span>📍 {eventDetail.venue.name}{eventDetail.venue.address ? `, ${eventDetail.venue.address}` : ''}{eventDetail.venue.city ? `, ${eventDetail.venue.city}` : ''}</span>
            )}
            {eventDetail.mealInfo && <span>🍽️ {eventDetail.mealInfo}</span>}
            {eventDetail.transportInfo && <span>🚌 {eventDetail.transportInfo}</span>}
          </div>
          {eventDetail.venue && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([eventDetail.venue.name, eventDetail.venue.address, eventDetail.venue.city, eventDetail.venue.state].filter(Boolean).join(', '))}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', marginTop: '0.6rem', padding: '0.5rem 1.2rem', backgroundColor: '#1a73e8', color: '#fff', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}
            >
              📍 Get Directions
            </a>
          )}
        </div>
      )}

      {/* Parking Section */}
      {eventDetail?.venue?.parkingInfo && (
        <div style={{ backgroundColor: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#5b21b6', margin: '0 0 0.4rem' }}>🅿️ Parking Information</h3>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#374151', lineHeight: 1.5 }}>{eventDetail.venue.parkingInfo}</p>
        </div>
      )}

      {/* Venue Contact */}
      {eventDetail?.venue?.contactName && (
        <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1d4ed8', margin: '0 0 0.4rem' }}>📞 Venue Contact</h3>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#374151' }}>
            {eventDetail.venue.contactName}
            {eventDetail.venue.contactPhone && ` · ${eventDetail.venue.contactPhone}`}
          </p>
        </div>
      )}

      <div style={styles.header} className="event-agenda-header">
        <h1 style={{ ...styles.title, fontWeight: 800, textTransform: 'uppercase' as const, fontSize: '1.3rem' }}>
          {eventDetail ? `${eventDetail.name} AGENDA` : '📋 Agenda'}
        </h1>
        {canManage && (
          <button style={styles.addButton} className="touch-target" onClick={openCreate}>+ Add Item</button>
        )}
      </div>

      {loading && <p style={styles.loading}>Loading agenda...</p>}

      {!loading && agendaItems.length === 0 && (
        <div style={styles.empty}>
          <p>No agenda items yet.</p>
          {canManage && <p>Click "Add Item" to create the first agenda entry.</p>}
        </div>
      )}

      {!loading && agendaItems.length > 0 && (
        <div className="event-agenda-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {agendaItems.map((item, index) => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'stretch', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }} className="event-agenda-item">
              {/* Order number — left of the bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px', backgroundColor: '#f0fdf4', color: '#1a6b3c', fontSize: '0.9rem', fontWeight: 700, flexShrink: 0 }}>
                {index + 1}
              </div>
              {/* Vertical separator bar */}
              <div style={{ width: '3px', backgroundColor: '#1a6b3c', flexShrink: 0 }} />
              {/* Agenda content */}
              <div style={{ padding: '0.75rem 1rem', flex: 1, minWidth: 0 }}>
                <div style={styles.itemTime}>
                  {formatAgendaTime(item.startTime)} – {formatAgendaTime(item.endTime)}
                </div>
                <div style={styles.itemTitle}>{item.title}</div>
                {item.description && (
                  <div style={styles.itemDescription}>{item.description}</div>
                )}
                {item.speaker && (
                  <div style={styles.itemSpeaker}>🎤 {item.speaker}</div>
                )}
                {item.details && item.details.trim() && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => toggleExpanded(item.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '0.8rem', fontWeight: 600, color: '#1a6b3c', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                      aria-expanded={expandedIds.has(item.id)}
                      aria-label={expandedIds.has(item.id) ? `Hide details for ${item.title}` : `Show more info for ${item.title}`}
                    >
                      {expandedIds.has(item.id) ? '▲ Hide info' : 'ℹ️ More info'}
                    </button>
                    {expandedIds.has(item.id) && (
                      <div
                        style={{ marginTop: '0.5rem', padding: '0.75rem 0.9rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.88rem', color: '#374151', lineHeight: 1.55 }}
                        dangerouslySetInnerHTML={{ __html: item.details }}
                      />
                    )}
                  </div>
                )}
              </div>
              {canManage && (
                <div style={{ ...styles.actions, padding: '0.5rem', flexDirection: 'column' as const, gap: '2px' }} className="event-agenda-actions">
                  <button style={styles.actionBtn} className="touch-target" onClick={() => handleReorder(item, 'up')} disabled={index === 0} title="Move up">⬆️</button>
                  <button style={styles.actionBtn} className="touch-target" onClick={() => handleReorder(item, 'down')} disabled={index === agendaItems.length - 1} title="Move down">⬇️</button>
                  <button style={styles.actionBtn} className="touch-target" onClick={() => openEdit(item)} title="Edit">✏️</button>
                  <button style={{ ...styles.actionBtn, color: '#999' }} className="touch-target" onClick={() => handleDelete(item)} title="Delete">🗑️</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div style={styles.modal} onClick={() => setShowForm(false)}>
          <div style={styles.modalContent} className="event-agenda-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              {editingItem ? 'Edit Agenda Item' : 'Add Agenda Item'}
            </h2>

            {formError && <div style={styles.error}>{formError}</div>}

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label} htmlFor="agendaTitle">Title</label>
                  <input
                    id="agendaTitle"
                    style={styles.input}
                    maxLength={200}
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label} htmlFor="agendaStart">Start Time</label>
                  <input
                    id="agendaStart"
                    type="datetime-local"
                    style={styles.input}
                    value={form.startTime}
                    onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label} htmlFor="agendaEnd">End Time</label>
                  <input
                    id="agendaEnd"
                    type="datetime-local"
                    style={styles.input}
                    value={form.endTime}
                    onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label} htmlFor="agendaSpeaker">Speaker</label>
                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    <input
                      id="agendaSpeaker"
                      style={{ ...styles.input, flex: 1 }}
                      maxLength={200}
                      placeholder="Type name or select MC →"
                      value={form.speaker}
                      onChange={(e) => setForm({ ...form, speaker: e.target.value })}
                    />
                    <button
                      type="button"
                      style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, backgroundColor: '#f0f7f2', border: '1px solid #1a6b3c', borderRadius: '4px', color: '#1a6b3c', cursor: 'pointer', whiteSpace: 'nowrap' as const }}
                      onClick={() => setForm({ ...form, speaker: 'MC' })}
                      title="Set speaker as MC"
                    >
                      🎤 MC
                    </button>
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label} htmlFor="agendaOrder">Sort Order</label>
                  <input
                    id="agendaOrder"
                    type="number"
                    min="0"
                    style={styles.input}
                    value={form.sortOrder}
                    onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                  />
                </div>

                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label} htmlFor="agendaDesc">Description</label>
                  <textarea
                    id="agendaDesc"
                    style={styles.textarea}
                    maxLength={2000}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                  <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>Short line shown on the agenda row.</div>
                </div>

                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Details / Presenter Bios <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional)</span></label>
                  <RichTextEditor
                    value={form.details}
                    onChange={(html) => setForm({ ...form, details: html })}
                    placeholder="Longer description, presenter bios, session background… Use the toolbar for bold, lists, and links. Guests tap 'More info' to read this."
                    minHeight="260px"
                  />
                  <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>Formatting toolbar above (bold, italic, underline, lists, links). Hidden behind a collapsible “More info” on the guest agenda — leave blank if not needed.</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" style={styles.submitBtn} disabled={saving}>
                  {saving ? 'Saving...' : editingItem ? 'Update Item' : 'Create Item'}
                </button>
                <button type="button" style={styles.cancelBtn} onClick={() => setShowForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

